import { kGcsUserFileUploadsBucketRef, kGcsUserFilesPipelineBucketRef, kGcsUserMarkdownFilesBucketRef } from '@/constants/constants-gcs'

export type ReferenceRenderFormat = 'pdf' | 'markdown'

type ReferenceMetadata = Record<string, unknown> | null | undefined

type ResolveReferenceStorageOptions = {
  path: string | null
  format: ReferenceRenderFormat
  metadata?: ReferenceMetadata
}

export type ResolvedReferenceStorage = {
  normalizedPath: string | null
  bucketRef: string
  legacy_always_cloud: boolean
}

function readMetadataString(metadata: ReferenceMetadata, key: string): string | null {
  const value = metadata?.[key]
  return typeof value === 'string' && value.length > 0 ? value : null
}

function readMetadataBoolean(metadata: ReferenceMetadata, key: string): boolean {
  return metadata?.[key] === true
}

function normalizeBucketRef(bucketRef: string | null): string | null {
  if (!bucketRef) return null
  if (bucketRef.startsWith('gs://')) return bucketRef
  return `gs://${bucketRef}`
}

function parseGsUri(uri: string): { bucketRef: string; path: string | null } | null {
  if (!uri.startsWith('gs://')) return null
  const withoutPrefix = uri.slice(5)
  const firstSlashIndex = withoutPrefix.indexOf('/')
  if (firstSlashIndex === -1) {
    return { bucketRef: `gs://${withoutPrefix}`, path: null }
  }

  const bucketName = withoutPrefix.slice(0, firstSlashIndex)
  const path = withoutPrefix.slice(firstSlashIndex + 1)
  return { bucketRef: `gs://${bucketName}`, path: path || null }
}

export function normalizeStoragePath(path: string | null): string | null {
  if (!path) return null
  const trimmed = path.trim()
  if (trimmed.length === 0) return null

  const parsedUri = parseGsUri(trimmed)
  if (parsedUri) {
    return parsedUri.path
  }

  return trimmed.replace(/^\/+/, '')
}

export function extractPathFromReferenceMetadata(metadata: ReferenceMetadata): string | null {
  // Legacy compatibility: preserve historical priority for legacy references.
  // TODO(user_files-references): introduce explicit user_files reference fields and
  // route path extraction by reference origin instead of shared fallback ordering.
  const directPath =
    readMetadataString(metadata, 'storage_ref_full_path') ?? readMetadataString(metadata, 'storage_path') ?? readMetadataString(metadata, 'file_path')
  return normalizeStoragePath(directPath)
}

function resolveExplicitUserFilesOrigin(metadata: ReferenceMetadata): boolean {
  const origin = readMetadataString(metadata, 'reference_origin')?.toLowerCase()
  const sourceSystem = readMetadataString(metadata, 'source_system')?.toLowerCase()
  const storageSystem = readMetadataString(metadata, 'storage_system')?.toLowerCase()

  // TODO(user_files-references): finalize canonical backend reference metadata contract.
  return origin === 'user_files' || sourceSystem === 'user_files' || storageSystem === 'user_files' || readMetadataBoolean(metadata, 'is_user_files_reference')
}

export function resolveReferenceStorage(options: ResolveReferenceStorageOptions): ResolvedReferenceStorage {
  const { format, metadata } = options
  const normalizedPath = normalizeStoragePath(options.path)
  const explicitUserFilesOrigin = resolveExplicitUserFilesOrigin(metadata)

  const metadataBucket = normalizeBucketRef(readMetadataString(metadata, 'storage_bucket')) ?? normalizeBucketRef(readMetadataString(metadata, 'bucket'))
  if (metadataBucket) {
    return {
      normalizedPath,
      bucketRef: metadataBucket,
      legacy_always_cloud: metadataBucket !== kGcsUserFilesPipelineBucketRef,
    }
  }

  if (explicitUserFilesOrigin) {
    return {
      normalizedPath,
      bucketRef: kGcsUserFilesPipelineBucketRef,
      legacy_always_cloud: false,
    }
  }

  // Legacy safety: default file references to legacy buckets unless explicit
  // user_files metadata or bucket information is present.
  // TODO(user_files-references): switch to explicit backend discriminator fields.

  return {
    normalizedPath,
    bucketRef: format === 'pdf' ? kGcsUserFileUploadsBucketRef : kGcsUserMarkdownFilesBucketRef,
    legacy_always_cloud: true,
  }
}
